import { useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/db73m4f799gd4v2febcg';

const values = [
  { icon: 'ri-fire-line', title: '热门优先', desc: '持续追踪各大平台热播新剧，第一时间整理推荐与剧情看点。' },
  { icon: 'ri-book-open-line', title: '剧情清晰', desc: '用简洁易懂的语言概括故事主线，帮你快速判断是否值得追。' },
  { icon: 'ri-calendar-check-line', title: '更新及时', desc: '同步更新进度与播出时间，不错过每一集的更新时间。' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot anti-spam check
    const honey = String(formData.get('company_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'company_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      payload.append(key, value as string);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = /spam/i.test(String(serverMsg));
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(String(serverMsg) || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full">
      {/* hero */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20media%20wall%20with%20glowing%20television%20screens%20and%20film%20strips%20in%20deep%20blue%20tones%20with%20warm%20orange%20highlights%2C%20minimal%20modern%20studio%20atmosphere%2C%20soft%20focus%20background%2C%20editorial%20entertainment%20branding%20visual%2C%20wide%20composition&width=1600&height=600&seq=about-hero-band&orientation=landscape"
          alt="关于最新电视剧"
          title="关于最新电视剧网站"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/75 to-primary-950/50" />
        <div className="relative z-10 mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-300" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-400">关于我们</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            关于「最新电视剧」
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            我们是一个专注电视剧资讯与推荐的内容站点，致力于为追剧用户整理当下最热门的剧集、清晰的剧情介绍与即时的播出信息，
            让每一位观众都能轻松找到想看的好剧。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        {/* intro */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950">网站介绍</h2>
            </div>
            <div className="space-y-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              <p>
                最新电视剧网站面向广大的影视剧爱好者与娱乐资讯关注者，围绕「热门新剧推荐」与「剧情介绍」两大主题，
                提供国产剧、韩剧、美剧、古装剧、都市剧等多类型剧集的浏览与检索服务。
              </p>
              <p>
                我们希望帮助用户快速查找热门新剧、了解剧集详情、主演阵容、播出时间与更新状态，
                用清晰的信息层级和舒适的阅读体验，让追剧这件事变得简单而愉快。
              </p>
              <p className="rounded-lg bg-background-100 p-4 text-sm text-foreground-600">
                说明：本站剧集资料、评分与热度均为虚构示例数据，仅用于展示网站功能，不代表真实播出与评价信息。
                评论、收藏等互动功能为演示用途，其中收藏仅保存在你的本机浏览器。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-primary-50 p-6">
            <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-flag-2-line text-primary-600" />
              我们的目标
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-foreground-700">
              {['聚合最新最热的电视剧推荐', '提供准确清晰的剧情与信息', '打造舒适易读的浏览体验', '陪伴每一位观众追好剧'].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* values */}
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className={`${v.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
            </div>
          ))}
        </div>

        {/* contact */}
        <div id="contact" className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950">联系我们</h2>
            </div>
            <p className="text-sm leading-7 text-foreground-700">
              如果你对网站内容有任何建议，或希望推荐一部好剧，欢迎通过下方表单给我们留言，我们会认真阅读每一条反馈。
            </p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-mail-line text-lg" />
                </span>
                contact@zuixindianshiju.example
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-customer-service-2-line text-lg" />
                </span>
                工作日 09:00 - 18:00 在线
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-map-pin-line text-lg" />
                </span>
                线上服务 · 内容合作请邮件沟通
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <form
              data-readdy-form
              id="about-contact-form"
              onSubmit={onSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收我们的回复"
                    className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  反馈类型
                </label>
                <select
                  id="topic"
                  name="topic"
                  defaultValue="内容建议"
                  className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 focus:border-primary-300 focus:outline-none"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="好剧推荐">好剧推荐</option>
                  <option value="问题反馈">问题反馈</option>
                  <option value="内容合作">内容合作</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请输入你的建议或想推荐的剧集（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-100 p-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
                />
              </div>

              <div className="form-field-extra" aria-hidden="true">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-5 h-11 w-full whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-8"
              >
                {status === 'submitting' ? '提交中…' : '提交反馈'}
              </button>

              {status === 'success' && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-fill" />
                  提交成功，感谢你的反馈！
                </p>
              )}
              {status === 'error' && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-background-200 px-4 py-3 text-sm text-foreground-800">
                  <i className="ri-error-warning-fill text-accent-600" />
                  {formError || '提交失败，请稍后重试。'}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* privacy */}
        <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="mb-4 flex items-center gap-2">
            <span className="h-6 w-1.5 rounded-full bg-accent-500" />
            <h2 className="font-heading text-xl font-bold text-foreground-950">版权声明与隐私说明</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 text-sm leading-7 text-foreground-700 md:grid-cols-2">
            <div>
              <h3 className="mb-2 font-heading text-base font-semibold text-foreground-900">版权声明</h3>
              <p>
                本站所有剧集名称、剧情简介、演职人员等资料均为虚构示例内容，仅用于网站功能演示，
                不构成任何真实作品或商业宣传。若涉及第三方内容，请以官方发布信息为准。
              </p>
            </div>
            <div>
              <h3 className="mb-2 font-heading text-base font-semibold text-foreground-900">隐私说明</h3>
              <p>
                我们仅收集你在反馈表单中主动填写的信息，用于回复你的留言；
                收藏记录仅保存在你自己的浏览器本地，不会被上传到服务器。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}